/**
 * Contextual help content schema — see
 * bmc/docs/requirements/contextual-help-system.md.
 *
 * All user-facing help copy lives in the JSON files in this folder; components
 * never hardcode help strings. English only for now, but the per-file layout
 * keeps a future `hi` variant possible by swapping files.
 */

export interface StepHelpEntry {
  /** Short heading for the panel. */
  title: string;
  /** What the user is doing on this step. */
  what: string;
  /** What good input looks like / what is required. */
  expectation: string;
  /** A concrete good example (rendered truncated with "show more"). */
  example?: string | null;
  /** What happens after this step (e.g. admin review). */
  next?: string | null;
  /** Tooltip text per field key, consumed by <FieldHint>. */
  fields?: Record<string, string>;
  /**
   * Offering-wizard only: per-offering-type overrides. Unspecified keys fall
   * back to the base entry.
   */
  overrides?: Record<string, Partial<Omit<StepHelpEntry, "overrides">>>;
}

export type HelpContent = Record<string, StepHelpEntry>;

/** Resolve a step entry, applying an offering-type override when present. */
export function resolveHelpEntry(
  content: HelpContent,
  stepId: string,
  overrideKey?: string | null,
): StepHelpEntry | null {
  const base = content[stepId];
  if (!base) return null;
  const override = overrideKey ? base.overrides?.[overrideKey] : undefined;
  return override ? { ...base, ...override, fields: { ...base.fields, ...override.fields } } : base;
}
